// DeferredActionLogList.jsx
import React from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * @param {Object[]} logs - список логов
 * @param {number}   logs[].id
 * @param {"SUCCESS"|"CHECK_CONDITION_FAILED"|"FAILED"} logs[].status
 * @param {string}   logs[].error
 * @param {string}   logs[].createdAt - ISO datetime
 * @param {Object}   logs[].user - ReservationDto.UserDto (произвольная структура)
 */
export default function DeferredActionLogList({ logs = [] }) {
    const fmtDate = (iso) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso || "—";
        }
    };

    const statusChip = (status) => {
        switch (status) {
            case "SUCCESS":
                return (
                    <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        label="SUCCESS"
                    />
                );
            case "CHECK_CONDITION_FAILED":
                return (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<CancelIcon />}
                        label="CONDITION FAILED"
                    />
                );
            case "FAILED":
            default:
                return (
                    <Chip
                        size="small"
                        color="error"
                        icon={<ErrorOutlineIcon />}
                        label="FAILED"
                    />
                );
        }
    };

    const renderUser = (u) => {
        if (!u) return <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>;

        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{"U"}</Avatar>
                <Box>
                    <Typography variant="body2">{`ID: ${u.id}`}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Telegram:{u.telegramId}
                    </Typography>
                </Box>
            </Stack>
        );
    };

    const renderError = (err) => {
        if (!err) return <Typography variant="body2" sx={{ opacity: 0.7 }}>—</Typography>;
        const short = String(err);
        const truncated = short.length > 120 ? short.slice(0, 120) + "…" : short;
        const showTooltip = short.length > 120;
        const content = (
            <Typography variant="body2" component="span">
                <code style={{ whiteSpace: "pre-wrap" }}>{truncated}</code>
            </Typography>
        );
        return showTooltip ? (
            <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{short}</span>} arrow>
                {content}
            </Tooltip>
        ) : (
            content
        );
    };

    return (
        <Card>
            <CardContent>
                <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">Логи выполнения</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Всего: {logs.length}
                    </Typography>
                </Stack>

                {logs.length === 0 ? (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Логи отсутствуют.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={80}>ID</TableCell>
                                    <TableCell width={160}>Статус</TableCell>
                                    <TableCell>Результат</TableCell>
                                    <TableCell width={260}>Пользователь</TableCell>
                                    <TableCell width={200}>Создано</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{log.id}</TableCell>
                                        <TableCell>{statusChip(log.status)}</TableCell>
                                        <TableCell>{renderError(log.error)}</TableCell>
                                        <TableCell>{renderUser(log.user)}</TableCell>
                                        <TableCell>{fmtDate(log.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
